import { useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ConfirmationDialog } from "@/components/dashboard/ConfirmationDialog";

export interface Version {
  id: string;
  name: string;
  createdAt: Date;
  isLatest: boolean;
}

interface VersionHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectVersion: (version: Version) => void;
  onDeleteVersion: (version: Version) => Promise<void>;
  versions: Version[];
  currentVersionId?: string | null;
}

export function VersionHistoryModal({
  open,
  onOpenChange,
  onSelectVersion,
  onDeleteVersion,
  versions,
  currentVersionId,
}: VersionHistoryModalProps) {
  const [deletingVersion, setDeletingVersion] = useState<Version | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deletingVersion) return;
    
    setIsDeleting(true);
    try {
      await onDeleteVersion(deletingVersion);
      setDeletingVersion(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          <DialogDescription>
            Select a version to view.
          </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-2">
              {versions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No versions yet</p>
              ) : (
                versions.map((version) => {
                  const isCurrentlyViewing = version.id === currentVersionId;
                  return (
                    <div
                      key={version.id}
                      className={cn(
                        "w-full p-4 text-left border rounded-2xl transition-colors group relative",
                        isCurrentlyViewing 
                          ? "border-accent bg-accent/10" 
                          : "border-foreground/10 hover:bg-foreground/5"
                      )}
                    >
                      <button
                        onClick={() => onSelectVersion(version)}
                        className="w-full text-left"
                      >
                        <div className="flex items-center justify-between pr-8">
                          <div className="flex items-center gap-2">
                            {isCurrentlyViewing && (
                              <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                                <Check className="w-3 h-3 text-accent-foreground" />
                              </div>
                            )}
                            <span className={cn(
                              "font-medium text-foreground",
                              !isCurrentlyViewing && "group-hover:underline"
                            )}>
                              {version.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isCurrentlyViewing && (
                              <Badge variant="outline" className="border-accent text-accent-foreground">
                                Viewing
                              </Badge>
                            )}
                            {version.isLatest && (
                              <Badge className="bg-accent text-accent-foreground hover:bg-accent">
                                Latest
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className={cn(
                          "text-sm text-muted-foreground mt-1",
                          isCurrentlyViewing && "ml-7"
                        )}>
                          {format(version.createdAt, "MMMM d, yyyy 'at' h:mm a")}
                        </p>
                      </button>
                      
                      {/* Delete button - positioned absolutely on the right */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingVersion(version);
                        }}
                        disabled={isDeleting}
                        className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-destructive/10 rounded-lg transition-all disabled:opacity-50"
                        title="Delete version"
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive transition-colors" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={!!deletingVersion}
        onOpenChange={(open) => !open && setDeletingVersion(null)}
        title="Delete Version?"
        description={`Are you sure you want to delete ${deletingVersion?.name}? This action cannot be undone. All comments on this version will also be deleted.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        variant="destructive"
      />
    </>
  );
}
